import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Rocket, RefreshCw, ExternalLink, FileText, AlertTriangle, CheckCircle2,
  Gauge, ListChecks, Map as MapIcon, ShieldCheck, Search, Sparkles, Undo2, Loader2,
  KeyRound, Lightbulb, Globe, Plus, Trash2, Zap, Wrench, Activity, Image as ImageIcon, ArrowLeftRight, ShoppingBag, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import IssuesCentre from "./seo/IssuesCentre";
import JobsMonitor from "./seo/JobsMonitor";
import EntityEditors from "./seo/EntityEditors";
import SeoSettings from "./seo/SeoSettings";
import BrandingTab from "./seo/BrandingTab";
import RedirectsTab from "./seo/RedirectsTab";
import SchemaTab from "./seo/SchemaTab";
import MerchantTab from "./merchant/MerchantTab";
import SeoSyncTab from "./seo/SeoSyncTab";
import UrlRepairTab from "./seo/url/UrlRepairTab";
import ServicesAdmin from "./seo/cms/ServicesAdmin";
import ArticlesAdmin from "./seo/cms/ArticlesAdmin";
import LinkManager from "./seo/links/LinkManager";
import OverviewTab from "./seo/OverviewTab";

const ISSUE_LABELS = {
  missing_title: "Missing title", poor_title_short: "Title too short", poor_title_long: "Title too long",
  missing_meta: "Missing meta description", thin_content: "Thin content", missing_image: "Missing image",
  missing_brand: "Missing brand", missing_category: "Missing category",
};
const STATUS_STYLE = {
  INDEX_READY: "bg-emerald-100 text-emerald-700",
  IMPROVEMENT_REQUIRED: "bg-amber-100 text-amber-700",
  NOINDEX_RECOMMENDED: "bg-slate-200 text-slate-600",
};

export default function SeoTab() {
  const [view, setView] = useState("overview");
  const [issueCat, setIssueCat] = useState("missing_meta");
  const openIssue = (cat) => { setIssueCat(cat); setView("issues"); };
  return (
    <div data-testid="seo-autopilot">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center w-10 h-10 rounded-lg bg-accent text-steel"><Rocket size={20} /></span>
        <div>
          <h1 className="text-2xl font-extrabold text-steel">SEO Management</h1>
          <p className="text-sm text-muted-foreground">Crawl · analyse · fix · approve · verify · monitor</p>
        </div>
      </div>
      <div className="flex gap-1 mb-6 border-b overflow-x-auto">
        {[["overview", "SEO Overview", Gauge], ["dashboard", "Product SEO", Gauge], ["services", "Services", Wrench], ["articles", "Articles", FileText], ["links", "Link Manager", ArrowLeftRight], ["editors", "SEO Editors", FileText],
          ["issues", "Issues & Fixes", Wrench], ["jobs", "Jobs", Activity],
          ["audit", "Audit & Indexing", ListChecks], ["keywords", "Keywords", KeyRound],
          ["content", "Content Ideas", Lightbulb], ["sitemaps", "Sitemaps", MapIcon],
          ["branding", "Branding", ImageIcon], ["urlrepair", "URL Repair & Redirects", ArrowLeftRight], ["redirects", "robots.txt", FileText],
          ["schema", "Rich Results", Sparkles], ["merchant", "Google Merchant", ShoppingBag], ["brandsync", "Brand SEO Sync", Wand2],
          ["automation", "Automation", ShieldCheck], ["gsc", "Search Console", Globe]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`seo-subtab-${id}`} onClick={() => setView(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
              view === id ? "border-accent text-steel" : "border-transparent text-muted-foreground hover:text-steel"}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>
      {view === "dashboard" && <Dashboard onOpenIssue={openIssue} />}
      {view === "editors" && <EntityEditors />}
      {view === "issues" && <IssuesCentre initialCategory={issueCat} onJobStarted={() => setView("jobs")} />}
      {view === "jobs" && <JobsMonitor />}
      {view === "branding" && <BrandingTab />}
      {view === "redirects" && <RedirectsTab />}
      {view === "urlrepair" && <UrlRepairTab />}
      {view === "services" && <ServicesAdmin />}
      {view === "overview" && <OverviewTab go={setView} />}
      {view === "links" && <LinkManager />}
      {view === "articles" && <ArticlesAdmin />}
      {view === "schema" && <SchemaTab />}
      {view === "merchant" && <MerchantTab />}
      {view === "brandsync" && <SeoSyncTab />}
      {view === "automation" && <SeoSettings />}
      {view === "audit" && <Audit />}
      {view === "keywords" && <Keywords />}
      {view === "content" && <ContentOps />}
      {view === "sitemaps" && <Sitemaps />}
      {view === "gsc" && <SearchConsole />}
    </div>
  );
}

function Dashboard({ onOpenIssue }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/admin/seo/dashboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  useEffect(() => { load(); }, [load]);
  if (d === false) return <p className="text-sm text-destructive">Could not load SEO dashboard.</p>;
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const m = d.metrics;
  const health = m.total_products ? Math.round((m.index_ready / m.total_products) * 100) : 0;
  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-white p-5 flex items-center gap-5" data-testid="seo-health">
        <div className="relative w-20 h-20 shrink-0">
          <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
            <circle cx="18" cy="18" r="16" fill="none" stroke="#e2e8f0" strokeWidth="3" />
            <circle cx="18" cy="18" r="16" fill="none" stroke={health >= 70 ? "#059669" : health >= 40 ? "#d97706" : "#dc2626"}
              strokeWidth="3" strokeDasharray={`${health} 100`} strokeLinecap="round" pathLength="100" />
          </svg>
          <span className="absolute inset-0 grid place-items-center font-extrabold text-steel">{health}%</span>
        </div>
        <div>
          <div className="font-bold text-steel">SEO Health — index-ready products</div>
          <p className="text-sm text-muted-foreground">{m.index_ready.toLocaleString()} of {m.total_products.toLocaleString()} products meet index-quality rules.</p>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck size={13} className="text-slate-400" /> Google Search Console: Not connected (using internal crawl data)
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Index ready" value={m.index_ready} tone="good" testid="stat-index-ready" onClick={() => onOpenIssue("index_ready")} />
        <Stat label="Improvement required" value={m.improvement_required} tone="warn" testid="stat-improve" onClick={() => onOpenIssue("thin_content")} />
        <Stat label="No-index" value={m.noindex} tone="muted" testid="stat-noindex" onClick={() => onOpenIssue("noindex")} />
        <Stat label="In sitemap" value={m.sitemap_included} tone="good" testid="stat-in-sitemap" onClick={() => onOpenIssue("in_sitemap")} />
        <Stat label="Missing meta description" value={m.missing_meta} tone={m.missing_meta ? "warn" : "good"} testid="stat-missing-meta" onClick={() => onOpenIssue("missing_meta")} />
        <Stat label="Generic description on products" value={m.generic_meta} tone={m.generic_meta ? "crit" : "good"} testid="stat-generic-meta" onClick={() => onOpenIssue("generic_meta")} />
        <Stat label="Thin content" value={m.thin_content} tone={m.thin_content ? "warn" : "good"} testid="stat-thin" onClick={() => onOpenIssue("thin_content")} />
        <Stat label="Missing image" value={m.missing_image} tone={m.missing_image ? "crit" : "good"} testid="stat-missing-image" onClick={() => onOpenIssue("missing_image")} />
        <Stat label="Duplicate titles" value={m.duplicate_titles} tone={m.duplicate_titles ? "warn" : "good"} testid="stat-dup-titles" onClick={() => onOpenIssue("duplicate_titles")} />
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={load} data-testid="seo-refresh"><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
        <Button size="sm" className="bg-accent text-steel hover:bg-amber-600 font-semibold" onClick={() => onOpenIssue("missing_meta")} data-testid="seo-open-meta"><Zap size={14} className="mr-1.5" /> Fix missing meta</Button>
      </div>
    </div>
  );
}

function Stat({ label, value, tone, testid, onClick }) {
  const tones = { good: "text-emerald-600", warn: "text-amber-600", crit: "text-red-600", muted: "text-slate-500" };
  return (
    <button type="button" onClick={onClick} disabled={!onClick}
      className={`text-left rounded-xl border bg-white p-4 transition-all ${onClick ? "hover:shadow-md hover:border-accent cursor-pointer" : "cursor-default"}`} data-testid={testid}>
      <div className={`text-3xl font-extrabold font-mono ${tones[tone] || "text-steel"}`}>{Number(value || 0).toLocaleString()}</div>
      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">{label}{onClick && <span className="text-accent">→</span>}</div>
    </button>
  );
}

function Audit() {
  const [data, setData] = useState(null);
  const [issue, setIssue] = useState("");
  const [aiProduct, setAiProduct] = useState(null);
  const load = useCallback(() => {
    setData(null);
    api.get("/admin/seo/audit", { params: { limit: 150, ...(issue ? { issue } : {}) } })
      .then((r) => setData(r.data)).catch(() => setData(false));
  }, [issue]);
  useEffect(() => { load(); }, [load]);

  const setFlag = async (id, field, value) => {
    try {
      await api.patch(`/admin/seo/products/${id}`, { [field]: value });
      setData((d) => ({ ...d, items: d.items.map((it) => it.id === id ? { ...it, [field]: value } : it) }));
      toast.success("SEO setting saved");
    } catch { toast.error("Could not save"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="seo-issue-filter" value={issue} onChange={(e) => setIssue(e.target.value)}
          className="h-9 rounded-md border px-3 text-sm bg-white">
          <option value="">All issues</option>
          {Object.entries(ISSUE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={load} data-testid="seo-audit-refresh"><RefreshCw size={14} className="mr-1.5" /> Re-scan</Button>
        <span className="text-sm text-muted-foreground">Approval-first — AUTO follows index-quality rules; your override always wins.</span>
      </div>
      {data === false ? <p className="text-sm text-destructive">Could not load audit.</p>
        : !data ? <p className="text-sm text-muted-foreground">Scanning catalogue…</p>
        : data.items.length === 0 ? (
          <div className="rounded-xl border bg-white p-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <CheckCircle2 className="text-emerald-500" /> No issues found for this filter.
          </div>
        ) : (
          <div className="rounded-xl border bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="seo-audit-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3">Product</th><th className="p-3">Issues</th><th className="p-3">Index status</th>
                  <th className="p-3">Indexing</th><th className="p-3">Sitemap</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr key={it.id} className="border-t align-top" data-testid={`seo-row-${it.sku}`}>
                    <td className="p-3">
                      <div className="font-semibold text-steel line-clamp-1 max-w-xs">{it.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{it.sku}</div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {it.issues.map((i) => (
                          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 text-[11px] px-2 py-0.5">
                            <AlertTriangle size={10} /> {ISSUE_LABELS[i] || i}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[it.index_status]}`}>{it.index_status.replace(/_/g, " ")}</span></td>
                    <td className="p-3">
                      <select data-testid={`seo-index-${it.sku}`} value={it.seo_index} onChange={(e) => setFlag(it.id, "seo_index", e.target.value)}
                        className="h-8 rounded border px-2 text-xs bg-white">
                        <option value="auto">AUTO</option><option value="index">INDEX</option><option value="noindex">NOINDEX</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <select data-testid={`seo-sitemap-${it.sku}`} value={it.seo_in_sitemap} onChange={(e) => setFlag(it.id, "seo_in_sitemap", e.target.value)}
                        className="h-8 rounded border px-2 text-xs bg-white">
                        <option value="auto">AUTO</option><option value="yes">YES</option><option value="no">NO</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" className="h-8 px-2.5 border-accent text-steel hover:bg-amber-50"
                          data-testid={`seo-ai-${it.sku}`} onClick={() => setAiProduct(it)}>
                          <Sparkles size={14} className="mr-1 text-accent" /> AI Optimise
                        </Button>
                        <a href={`/product/${it.slug || it.id}`} target="_blank" rel="noreferrer" className="text-steel hover:text-accent p-1"><ExternalLink size={16} /></a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {aiProduct && <AiOptimiseDialog product={aiProduct} onClose={() => setAiProduct(null)} />}
    </div>
  );
}

function AiOptimiseDialog({ product, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({ seo_title: "", meta_description: "", description: "" });
  const [saving, setSaving] = useState(false);

  const generate = useCallback(() => {
    setLoading(true); setData(null);
    api.post(`/admin/seo/products/${product.id}/ai-optimise`)
      .then((r) => {
        setData(r.data);
        const rec = r.data.recommended || {};
        setEdit({ seo_title: rec.seo_title || "", meta_description: rec.meta_description || "", description: rec.description_html || "" });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "AI failed — try again"))
      .finally(() => setLoading(false));
  }, [product.id]);
  useEffect(() => { generate(); }, [generate]);

  const apply = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/seo/products/${product.id}/apply`, edit);
      toast.success("SEO changes applied"); onClose();
    } catch { toast.error("Could not apply"); } finally { setSaving(false); }
  };
  const rollback = async () => {
    try { await api.post(`/admin/seo/products/${product.id}/rollback`); toast.success("Rolled back to previous version"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nothing to roll back"); }
  };

  const rec = data?.recommended || {};
  const cur = data?.current || {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="seo-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-accent" /> AI Optimise SEO — {product.name}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-16 grid place-items-center text-muted-foreground">
            <Loader2 className="animate-spin mb-2" /> ChatGPT is analysing this product…
          </div>
        ) : !data ? (
          <div className="py-10 text-center"><Button onClick={generate}><RefreshCw size={15} className="mr-1.5" /> Try again</Button></div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2 text-xs">
              {rec.primary_keyword && <span className="rounded-full bg-steel text-white px-2.5 py-1">Primary: {rec.primary_keyword}</span>}
              {rec.search_intent && <span className="rounded-full bg-slate-100 px-2.5 py-1">Intent: {rec.search_intent}</span>}
              {(rec.secondary_keywords || []).slice(0, 6).map((k) => <span key={k} className="rounded-full bg-amber-50 text-amber-700 px-2.5 py-1">{k}</span>)}
            </div>

            <Field label="SEO Title" current={cur.seo_title}>
              <Input data-testid="ai-title" value={edit.seo_title} onChange={(e) => setEdit({ ...edit, seo_title: e.target.value })} />
              <Counter n={edit.seo_title.length} max={60} />
            </Field>
            <Field label="Meta Description" current={cur.meta_description || "— none —"}>
              <Textarea data-testid="ai-meta" rows={2} value={edit.meta_description} onChange={(e) => setEdit({ ...edit, meta_description: e.target.value })} />
              <Counter n={edit.meta_description.length} max={155} />
            </Field>
            <Field label="Description (ACP-original)" current={<span dangerouslySetInnerHTML={{ __html: cur.description_html || "— none —" }} />}>
              <Textarea data-testid="ai-desc" rows={6} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
            </Field>

            {(rec.headings || []).length > 0 && (
              <ReadBlock title="Suggested headings"><ul className="list-disc ml-5 text-sm space-y-0.5">{rec.headings.map((h, i) => <li key={i}>{h}</li>)}</ul></ReadBlock>
            )}
            {(rec.faqs || []).length > 0 && (
              <ReadBlock title="Suggested FAQs">{rec.faqs.map((f, i) => <div key={i} className="text-sm mb-2"><div className="font-semibold text-steel">{f.q}</div><div className="text-muted-foreground">{f.a}</div></div>)}</ReadBlock>
            )}
            {(rec.image_alt) && <ReadBlock title="Image ALT text"><p className="text-sm">{rec.image_alt}</p></ReadBlock>}
            {(rec.internal_link_suggestions || []).length > 0 && (
              <ReadBlock title="Internal link ideas"><ul className="list-disc ml-5 text-sm space-y-0.5">{rec.internal_link_suggestions.map((l, i) => <li key={i}>{l}</li>)}</ul></ReadBlock>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-2 border-t sticky bottom-0 bg-white">
              <Button data-testid="ai-approve" onClick={apply} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={15} className="mr-1.5" />} Approve &amp; Apply
              </Button>
              <Button data-testid="ai-regenerate" variant="outline" onClick={generate}><RefreshCw size={15} className="mr-1.5" /> Regenerate</Button>
              {data.can_rollback && <Button data-testid="ai-rollback" variant="outline" onClick={rollback}><Undo2 size={15} className="mr-1.5" /> Roll back</Button>}
              <Button data-testid="ai-reject" variant="ghost" onClick={onClose}>Reject / Close</Button>
              <span className="text-xs text-muted-foreground ml-auto">Approval-first · edit anything before applying · specs never invented</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Keywords() {
  const [data, setData] = useState(null);
  const [nk, setNk] = useState({ keyword: "", group: "General", priority: 2, page: "" });
  const load = useCallback(() => { api.get("/admin/seo/keywords").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!nk.keyword.trim()) return;
    try { await api.post("/admin/seo/keywords", nk); setNk({ ...nk, keyword: "", page: "" }); toast.success("Keyword added"); load(); }
    catch { toast.error("Could not add"); }
  };
  const save = async (id, field, value) => { await api.patch(`/admin/seo/keywords/${id}`, { [field]: value }); load(); };
  const del = async (id) => { await api.delete(`/admin/seo/keywords/${id}`); load(); };
  if (data === false) return <p className="text-sm text-destructive">Could not load keywords.</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-white p-4 flex flex-wrap gap-2 items-end" data-testid="kw-add">
        <div className="flex-1 min-w-[200px]"><label className="text-xs text-muted-foreground">Keyword</label><Input value={nk.keyword} onChange={(e) => setNk({ ...nk, keyword: e.target.value })} placeholder="e.g. EFTPOS hire Auckland" data-testid="kw-input" /></div>
        <div><label className="text-xs text-muted-foreground">Group</label><Input value={nk.group} onChange={(e) => setNk({ ...nk, group: e.target.value })} className="w-32" /></div>
        <div><label className="text-xs text-muted-foreground">Priority</label>
          <select value={nk.priority} onChange={(e) => setNk({ ...nk, priority: +e.target.value })} className="h-10 rounded-md border px-2 text-sm bg-white block"><option value={1}>P1</option><option value={2}>P2</option><option value={3}>P3</option></select></div>
        <div className="flex-1 min-w-[160px]"><label className="text-xs text-muted-foreground">Assigned page (optional)</label><Input value={nk.page} onChange={(e) => setNk({ ...nk, page: e.target.value })} placeholder="/category/POS" /></div>
        <Button onClick={add} data-testid="kw-add-btn"><Plus size={15} className="mr-1.5" /> Add</Button>
      </div>
      <div className="rounded-xl border bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="kw-table">
          <thead className="bg-slate-50 text-left text-xs uppercase text-muted-foreground"><tr>
            <th className="p-3">Keyword</th><th className="p-3">Group</th><th className="p-3">Priority</th><th className="p-3">Assigned page</th><th className="p-3">Competing products</th><th className="p-3"></th></tr></thead>
          <tbody>
            {data.items.map((k) => (
              <tr key={k.id} className="border-t" data-testid={`kw-row-${k.id}`}>
                <td className="p-3 font-semibold text-steel">{k.keyword}{k.cannibalisation && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-50 text-red-600 text-[10px] px-2 py-0.5"><AlertTriangle size={9} /> cannibalisation</span>}</td>
                <td className="p-3">{k.group}</td>
                <td className="p-3"><span className={`rounded px-2 py-0.5 text-xs ${k.priority === 1 ? "bg-accent text-steel" : "bg-slate-100"}`}>P{k.priority}</span></td>
                <td className="p-3"><Input defaultValue={k.page} onBlur={(e) => e.target.value !== k.page && save(k.id, "page", e.target.value)} placeholder="—" className="h-8 w-40 text-xs" /></td>
                <td className="p-3"><span className={k.product_matches > 8 ? "text-amber-600 font-semibold" : "text-muted-foreground"}>{k.product_matches}</span></td>
                <td className="p-3"><button onClick={() => del(k.id)} className="text-muted-foreground hover:text-red-600" data-testid={`kw-del-${k.id}`}><Trash2 size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Cannibalisation is flagged when two keywords target the same page. "Competing products" shows how many catalogue items match the term (a high count may need a dedicated landing page).</p>
    </div>
  );
}

function ContentOps() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const generate = () => {
    setLoading(true);
    api.post("/admin/seo/content-opportunities").then((r) => setItems(r.data.opportunities)).catch((e) => toast.error(e?.response?.data?.detail || "AI failed")).finally(() => setLoading(false));
  };
  const TYPE = { service_page: "Service page", article: "Article", faq: "FAQ", buying_guide: "Buying guide", comparison: "Comparison", troubleshooting: "Troubleshooting" };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button onClick={generate} disabled={loading} data-testid="content-generate" className="bg-accent text-steel hover:bg-amber-600 font-semibold">{loading ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Lightbulb size={15} className="mr-1.5" />} Generate content ideas (ChatGPT)</Button>
        <span className="text-sm text-muted-foreground">Real customer-question ideas mapped to your categories — build pages that genuinely help NZ buyers.</span>
      </div>
      {loading && <p className="text-sm text-muted-foreground">Analysing your catalogue & search intent…</p>}
      {items && <div className="grid md:grid-cols-2 gap-4" data-testid="content-list">
        {items.map((o, i) => (
          <div key={i} className="rounded-xl border bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] uppercase tracking-wide rounded-full bg-steel text-white px-2 py-0.5">{TYPE[o.type] || o.type}</span>
              {o.primary_keyword && <span className="text-xs text-accent font-semibold">{o.primary_keyword}</span>}
            </div>
            <div className="font-bold text-steel mt-2">{o.title}</div>
            {o.search_intent && <div className="text-xs text-muted-foreground mt-0.5">Intent: {o.search_intent}</div>}
            {o.why && <p className="text-sm text-muted-foreground mt-1">{o.why}</p>}
            {(o.outline || []).length > 0 && <ul className="list-disc ml-5 text-sm mt-2 space-y-0.5">{o.outline.map((s, j) => <li key={j}>{s}</li>)}</ul>}
          </div>
        ))}
      </div>}
    </div>
  );
}

function SearchConsole() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ property_url: "", service_account_json: "" });
  const [perf, setPerf] = useState(null);
  const [opps, setOpps] = useState(null);
  const loadStatus = useCallback(() => { api.get("/admin/seo/gsc/status").then((r) => setStatus(r.data)).catch(() => setStatus(false)); }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (status?.connected) {
      api.get("/admin/seo/gsc/performance").then((r) => setPerf(r.data)).catch(() => {});
      api.get("/admin/seo/gsc/opportunities").then((r) => setOpps(r.data.items || [])).catch(() => {});
    }
  }, [status]);
  const connect = async () => {
    try { await api.post("/admin/seo/gsc/connect", form); toast.success("Search Console connected"); loadStatus(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invalid credentials"); }
  };
  const disconnect = async () => { await api.delete("/admin/seo/gsc/disconnect"); setPerf(null); setOpps(null); loadStatus(); };
  if (status === false) return <p className="text-sm text-destructive">Could not load status.</p>;
  if (!status) return <p className="text-sm text-muted-foreground">Loading…</p>;

  if (!status.connected) return (
    <div className="max-w-2xl rounded-xl border bg-white p-6 space-y-4" data-testid="gsc-connect">
      <div className="flex items-center gap-2 text-steel font-bold"><Globe size={18} /> Connect Google Search Console</div>
      <p className="text-sm text-muted-foreground">Paste a Google Cloud <b>service-account JSON</b> with access to your Search Console property, and the property URL. Credentials are stored securely server-side and never exposed to the browser.</p>
      <div><label className="text-xs text-muted-foreground">Property URL (exactly as in Search Console)</label><Input value={form.property_url} onChange={(e) => setForm({ ...form, property_url: e.target.value })} placeholder="https://acponline.co.nz/ or sc-domain:acponline.co.nz" data-testid="gsc-prop" /></div>
      <div><label className="text-xs text-muted-foreground">Service-account JSON</label><Textarea rows={6} value={form.service_account_json} onChange={(e) => setForm({ ...form, service_account_json: e.target.value })} placeholder='{ "type": "service_account", ... }' data-testid="gsc-json" className="font-mono text-xs" /></div>
      <Button onClick={connect} data-testid="gsc-connect-btn"><ShieldCheck size={15} className="mr-1.5" /> Connect securely</Button>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="rounded-xl border bg-white p-4 flex items-center justify-between">
        <div className="text-sm"><span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold"><CheckCircle2 size={15} /> Connected</span> <span className="text-muted-foreground ml-2">{status.property_url}</span></div>
        <Button variant="outline" size="sm" onClick={disconnect} data-testid="gsc-disconnect">Disconnect</Button>
      </div>
      {perf?.error && <p className="text-sm text-amber-600">{perf.error}</p>}
      {perf && !perf.error && <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Clicks (28d)" value={perf.clicks} tone="good" testid="gsc-clicks" />
        <Stat label="Impressions (28d)" value={perf.impressions} tone="good" testid="gsc-impr" />
        <Stat label="CTR %" value={perf.ctr} tone="good" testid="gsc-ctr" />
        <Stat label="Avg position" value={perf.avg_position} tone="warn" testid="gsc-pos" />
      </div>}
      {opps && opps.length > 0 && (
        <div className="rounded-xl border bg-white overflow-x-auto">
          <div className="p-3 font-semibold text-steel border-b">Opportunity Finder — quick wins</div>
          <table className="w-full text-sm"><thead className="bg-slate-50 text-left text-xs uppercase text-muted-foreground"><tr>
            <th className="p-3">Keyword</th><th className="p-3">Page</th><th className="p-3">Pos</th><th className="p-3">Impr</th><th className="p-3">CTR</th><th className="p-3">Recommended action</th></tr></thead>
            <tbody>{opps.map((o, i) => (<tr key={i} className="border-t"><td className="p-3">{o.keyword}</td><td className="p-3 max-w-xs truncate text-muted-foreground">{o.page}</td><td className="p-3">{o.position}</td><td className="p-3">{o.impressions}</td><td className="p-3">{o.ctr}%</td><td className="p-3 text-amber-700">{o.action}</td></tr>))}</tbody>
          </table>
        </div>
      )}
      {opps && opps.length === 0 && <p className="text-sm text-muted-foreground">No quick-win opportunities detected in the last 28 days yet.</p>}
    </div>
  );
}

function Field({ label, current, children }) {
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Current</div>
        <div className="rounded-lg border bg-slate-50 p-2.5 text-sm text-slate-600 min-h-[40px] max-h-40 overflow-y-auto">{current || "— none —"}</div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-accent font-semibold mb-1">AI recommended — {label}</div>
        {children}
      </div>
    </div>
  );
}
function Counter({ n, max }) {
  return <div className={`text-[11px] mt-0.5 text-right ${n > max ? "text-red-600" : "text-muted-foreground"}`}>{n}/{max}</div>;
}
function ReadBlock({ title, children }) {
  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{title}</div>
      {children}
    </div>
  );
}

function Sitemaps() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/admin/seo/sitemaps").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  useEffect(() => { load(); }, [load]);
  if (d === false) return <p className="text-sm text-destructive">Could not load sitemaps.</p>;
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Total URLs" value={d.total_urls} tone="good" testid="sm-total" />
        <Stat label="Products included" value={d.products_included} tone="good" testid="sm-prod-inc" />
        <Stat label="Products excluded" value={d.products_excluded} tone="muted" testid="sm-prod-exc" />
        <Stat label="Categories + Brands" value={d.categories_included + d.brands_included} tone="good" testid="sm-cat-brand" />
      </div>
      <div className="rounded-xl border bg-white p-5 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button asChild variant="outline" size="sm"><a href={d.index_url} target="_blank" rel="noreferrer" data-testid="sm-view-index"><ExternalLink size={14} className="mr-1.5" /> View sitemap index</a></Button>
          <Button asChild variant="outline" size="sm"><a href={d.robots_url} target="_blank" rel="noreferrer" data-testid="sm-view-robots"><FileText size={14} className="mr-1.5" /> robots.txt</a></Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="sm-refresh"><RefreshCw size={14} className="mr-1.5" /> Re-validate</Button>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <CheckCircle2 size={13} className="text-emerald-500" /> Auto-updates on every import, publish, discontinue & redirect. No manual rebuild needed. Admin URLs, cart, checkout, account & internal search are always excluded.
        </p>
        <div className="divide-y border rounded-lg">
          {d.children.map((c) => (
            <div key={c.loc} className="flex items-center justify-between p-3 text-sm">
              <span className="flex items-center gap-2 text-steel"><MapIcon size={14} className="text-slate-400" /> {c.name}</span>
              <span className="flex items-center gap-3">
                {c.urls != null && <span className="font-mono text-xs text-muted-foreground">{c.urls.toLocaleString()} URLs</span>}
                <a href={c.loc} target="_blank" rel="noreferrer" className="text-steel hover:text-accent"><ExternalLink size={15} /></a>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
